import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  Add,
  ClockBack,
  Delete,
  Download,
  LockLocked,
  Pencil,
  Person,
  Upload,
} from '@factorialco/f0-react/icons/app'
import {
  EmployeeCollectionFilterEmployeeGroupNamesDocument,
  EmployeeCollectionFilterJobTitlesDocument,
  EmployeeCollectionListEmployeeFragment,
  EmployeeCollectionListIdsEmployeeFragment,
  EmployeesCollectionListDocument,
} from 'generated/resources/graphql'
import uniqBy from 'lodash/uniqBy'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import {
  dateBackendDateTimeToRelativeTime,
  i18nDateToString,
  stringToDate,
} from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import {
  type BulkActionFilters,
  useEmployeesBulkActions,
} from 'modules/employees/components/EmployeeCollection/useEmployeesBulkActions'
import { useEmployeeFilters } from 'modules/employees/lib/filters'
import { extractWorkplaceFilterValues } from 'modules/employees/lib/filters/workplace/values'
import { toDateString } from 'modules/employees/lib/utils'
import { useExtractJobRoleFilterValues } from 'modules/job_catalog/lib/filters/jobRole/values'

// Runtime-safe checks for optional GraphQL fields without unsafe casts
const isRecord = (obj: unknown): obj is Record<string, unknown> => {
  return typeof obj === 'object' && obj !== null
}

const readContractFlags = (
  contract: unknown
): {
  isDiscontinuous: boolean
  isActive: boolean | undefined
  isPausable: boolean | undefined
  isResumable: boolean | undefined
} => {
  let isDiscontinuous = false
  let isActive: boolean | undefined = undefined
  let isPausable: boolean | undefined = undefined
  let isResumable: boolean | undefined = undefined

  if (isRecord(contract)) {
    const rawIsDiscontinuous = contract['isDiscontinuous']
    if (typeof rawIsDiscontinuous === 'boolean') {
      isDiscontinuous = rawIsDiscontinuous
    }

    const statusUnknown = contract['status']
    if (isRecord(statusUnknown)) {
      const rawIsActive = statusUnknown['isActive']
      if (typeof rawIsActive === 'boolean') {
        isActive = rawIsActive
      }
      const rawIsPausable = statusUnknown['isPausable']
      if (typeof rawIsPausable === 'boolean') {
        isPausable = rawIsPausable
      }
      const rawIsResumable = statusUnknown['isResumable']
      if (typeof rawIsResumable === 'boolean') {
        isResumable = rawIsResumable
      }
    }
  }

  return { isDiscontinuous, isActive, isPausable, isResumable }
}

const IDENTIFIER_TYPE_LABELS: Record<string, () => string> = {
  cnh: () => i18n.t('employees.identifier_type_short_labels.cnh'),
  cni: () => i18n.t('employees.identifier_type_short_labels.cni'),
  ci: () => i18n.t('employees.identifier_type_short_labels.ci'),
  cpf: () => i18n.t('employees.identifier_type_short_labels.cpf'),
  ctps: () => i18n.t('employees.identifier_type_short_labels.ctps'),
  de_id_card: () => i18n.t('employees.identifier_type_short_labels.de_id_card'),
  de_passport: () => i18n.t('employees.identifier_type_short_labels.de_passport'),
  dni: () => i18n.t('employees.identifier_type_short_labels.dni'),
  gb_driving_license: () => i18n.t('employees.identifier_type_short_labels.gb_driving_license'),
  gb_social_security: () => i18n.t('employees.identifier_type_short_labels.gb_social_security'),
  it_id_card: () => i18n.t('employees.identifier_type_short_labels.it_id_card'),
  it_residence_permit: () => i18n.t('employees.identifier_type_short_labels.it_residence_permit'),
  nie: () => i18n.t('employees.identifier_type_short_labels.nie'),
  nia: () => i18n.t('employees.identifier_type_short_labels.nia'),
  nir: () => i18n.t('employees.identifier_type_short_labels.nir'),
  ntt: () => i18n.t('employees.identifier_type_short_labels.ntt'),
  passport: () => i18n.t('employees.identifier_type_short_labels.passport'),
  pt_id_card: () => i18n.t('employees.identifier_type_short_labels.pt_id_card'),
  pt_residence_card: () => i18n.t('employees.identifier_type_short_labels.pt_residence_card'),
  rg: () => i18n.t('employees.identifier_type_short_labels.rg'),
  us_driving_license: () => i18n.t('employees.identifier_type_short_labels.us_driving_license'),
  us_social_security: () => i18n.t('employees.identifier_type_short_labels.us_social_security'),
}

export const translateCanonicalIdentifierType = (identifierType: string | null | undefined) => {
  if (!identifierType) return ''

  const labelResolver = IDENTIFIER_TYPE_LABELS[identifierType]

  if (labelResolver) return labelResolver()

  return ''
}

export const formatIdentifier = (
  identifier: string | null | undefined,
  identifierType: string | null | undefined
) => {
  if (!identifier) return ''

  const translatedIdentifierType = translateCanonicalIdentifierType(identifierType)

  if (!translatedIdentifierType) return identifier

  return {
    type: 'compound' as const,
    value: {
      separator: ' ',
      segments: [
        { type: 'text' as const, value: identifier },
        {
          type: 'text' as const,
          value: `(${translatedIdentifierType})`,
          tone: 'secondary' as const,
        },
      ],
    },
  }
}

export const lockIconRenderer = {
  type: 'icon' as const,
  value: {
    icon: LockLocked,
  },
}

const getContractStatus = (
  terminatedOn: string | null | undefined,
  referenceContract:
    | {
        startsOn?: string | null
        endsOn?: string | null
        contract?: unknown | null
      }
    | null
    | undefined
): string => {
  const today = new Date()

  if (!referenceContract) return 'ongoing'

  const { startsOn, endsOn, contract } = referenceContract

  // Check discontinuous contract status first, because for discontinuous contracts,
  // terminatedOn may be set to revoke access during inactivity periods, but the
  // contract itself is still valid (not ended)
  const flags = readContractFlags(contract)
  if (flags.isDiscontinuous) {
    if (flags.isActive === true) return 'in_activity_period'
    if (flags.isActive === false) return 'in_inactivity_period'
  }

  // Contract status is determined solely by contract dates, not employee termination
  if (endsOn && stringToDate(endsOn, 'backend').isBefore(today)) return 'ended'
  if (startsOn && stringToDate(startsOn, 'backend').isAfter(today)) return 'upcoming'

  return 'ongoing'
}

interface Props {
  canCreateEmployee: boolean
  canImportEmployees: boolean
  canEditEmployeesInBulk: boolean
  canImportResources: boolean
  canEditContracts: boolean
  canExportTimeoff: boolean
  canExportEmployees: boolean
  canManageTimeoff: boolean
  canImportTimeoffAllowances: boolean
  canBulkImportCustomTables: boolean
  canSeeFormerEmployees: boolean
  canSeeJobTitle: boolean
  canSeeJobCatalogTree: boolean
  canSeeJobCatalog: boolean
  hasLegacyJobCatalog: boolean
  canSeePendingPromotions: boolean
  canSeeStartDate: boolean
  canSeeEndDate: boolean
  hasCompensationsModule: boolean
  hasDatevIntegration: boolean
  hasPayrollManagementModule: boolean
  hasDevUniversalContractsEditionFeature: boolean
  hasDevUniversalContractsImportFeature: boolean
  hasDevFixedDiscontinuousContractsFeature: boolean
  hasDevUniversalUpdaterEmployeesFeature: boolean
  hasBecomeEnabledFeature: boolean
  hasFulltextEmployeeSearch?: boolean
  handleClickImportPeople: () => void
  handleClickImportContracts: () => void
  handleClickEditPeopleFromFile: () => void
  handleClickEditAgreementsFromFile: () => void
  handleDatevLaudsEmployeesImport: () => void
  handleClickEmployeesBulkEdition: (
    selectedEmployees: EmployeeCollectionListIdsEmployeeFragment[]
  ) => void
  handleClickContractsBulkEdition: (
    selectedEmployees: EmployeeCollectionListIdsEmployeeFragment[]
  ) => void
  handleClickAdditionalCompensationsBulkEdition: (
    selectedBulkEditEmployees: EmployeeCollectionListIdsEmployeeFragment[]
  ) => void
  handleClickSupplementsBulkEdition: (
    selectedBulkEditEmployees: EmployeeCollectionListIdsEmployeeFragment[]
  ) => void
  handleBecome: (employee: EmployeeCollectionListEmployeeFragment) => void
  handleInviteEmployee: (
    employee: EmployeeCollectionListEmployeeFragment,
    toggleRefetch?: () => void
  ) => void
  handleActivateEmployee: (
    employee: EmployeeCollectionListEmployeeFragment,
    toggleRefetch: () => void
  ) => void
  handleRequestBulkTerminate?: (payload: {
    selectedEmployeeIds: string[]
    eligibleEmployeeIds: string[]
    terminatedSelectedCount: number
  }) => void
  handleRequestBulkPause?: (payload: {
    selectedEmployees: EmployeeCollectionListEmployeeFragment[]
    eligibleEmployees: EmployeeCollectionListEmployeeFragment[]
  }) => void
  handleRequestBulkActivate?: (payload: {
    selectedEmployees: EmployeeCollectionListEmployeeFragment[]
    eligibleEmployees: EmployeeCollectionListEmployeeFragment[]
  }) => void
  handleRequestBulkFDContracts: (
    selectedEmployees: EmployeeCollectionListEmployeeFragment[]
  ) => void
}

const CollectionsPage = ({
  canCreateEmployee,
  canImportEmployees,
  canEditEmployeesInBulk,
  canImportResources,
  canEditContracts,
  canExportTimeoff,
  canExportEmployees,
  canManageTimeoff,
  canImportTimeoffAllowances,
  canBulkImportCustomTables,
  canSeeFormerEmployees,
  canSeeJobTitle,
  canSeeJobCatalogTree,
  canSeeJobCatalog,
  hasLegacyJobCatalog,
  canSeePendingPromotions,
  canSeeStartDate,
  canSeeEndDate,
  hasPayrollManagementModule,
  hasCompensationsModule,
  hasDatevIntegration,
  hasDevUniversalContractsEditionFeature,
  hasDevUniversalContractsImportFeature,
  hasDevFixedDiscontinuousContractsFeature,
  hasDevUniversalUpdaterEmployeesFeature,
  hasBecomeEnabledFeature,
  hasFulltextEmployeeSearch = false,
  handleClickEditPeopleFromFile,
  handleClickEditAgreementsFromFile,
  handleClickImportPeople,
  handleClickImportContracts,
  handleDatevLaudsEmployeesImport,
  handleClickEmployeesBulkEdition,
  handleClickContractsBulkEdition,
  handleClickAdditionalCompensationsBulkEdition,
  handleClickSupplementsBulkEdition,
  handleBecome,
  handleInviteEmployee,
  handleActivateEmployee,
  handleRequestBulkFDContracts,
  handleRequestBulkTerminate,
  handleRequestBulkPause,
  handleRequestBulkActivate,
}: Props) => {
  const client = useApolloClient()
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()
  const navigate = useNavigate()
  const [refetch, setRefetch] = useState(false)
  const [canSeeAnyJobTitle, setCanSeeAnyJobTitle] = useState(canSeeJobTitle)

  const { filters: employeeFilters, hasJobCatalogTreeFeature } = useEmployeeFilters({
    workplaceMode: 'locationAndWorkAreas',
  })
  const extractJobRoleFilterValues = useExtractJobRoleFilterValues({ hasJobCatalogTreeFeature })
  const { getSelectedEmployees } = useEmployeesBulkActions({
    hasJobCatalogTreeFeature,
    canSeeFormerEmployees,
  })
  const canExportData = canExportEmployees || canExportTimeoff

  const handleToggleRefetch = () => {
    setRefetch(!refetch)
  }

  const primaryActions = () => {
    return [
      canCreateEmployee && {
        label: i18n.t('employees.employees_list.actions.add_person'),
        icon: Add,
        onClick: () => {
          track(trackingEvents.employeesData.addEmployeeEvent, {
            company_id: company.id,
          })
          navigate(navigation.employees.list.add_employee.fullPath())
        },
      },
      canImportEmployees && {
        label: i18n.t('employees.employees_list.actions.import_people'),
        icon: Download,
        onClick: () => {
          track(trackingEvents.employeesData.importPeople, {
            company_id: company.id,
          })
          handleClickImportPeople()
        },
      },
      canEditContracts &&
        hasDevUniversalContractsImportFeature && {
          label: i18n.t('employees.employees_list.actions.import_agreements'),
          icon: Download,
          onClick: () => {
            track(trackingEvents.employeesData.importContracts, {
              company_id: company.id,
            })
            handleClickImportContracts()
          },
        },
    ].filter(Boolean)
  }

  const secondaryActions = () => {
    return [
      [
        canExportData && {
          label: i18n.t('employees.employees_list.actions.export_data'),
          icon: Upload,
          onClick: () => {
            track(trackingEvents.employeesData.exportData, {
              company_id: company.id,
            })
            if (hasDatevIntegration && canExportEmployees) {
              navigate(navigation.employees.list.exportTypeSelection.fullPath())
            } else {
              navigate(navigation.employees.list.export.fullPath())
            }
          },
        },
      ].filter(Boolean),
      [
        canBulkImportCustomTables && {
          label: i18n.t('employees.employees_list.actions.import_custom_tables'),
          icon: Download,
          onClick: () => {
            track(trackingEvents.employeesData.importDataTables, {
              company_id: company.id,
            })
            navigate(navigation.employees.list.customTablesImport.fullPath())
          },
        },
        canImportTimeoffAllowances && {
          label: i18n.t('employees.employees_list.actions.import_time_off_allowances'),
          icon: Download,
          onClick: () => {
            track(trackingEvents.employeesData.importTimeOffAllowances, {
              company_id: company.id,
            })
            navigate(navigation.employees.list.timeoff.fullPath())
          },
        },
        hasDatevIntegration && {
          label: i18n.t('employees.employees_list.actions.import_from_datev'),
          icon: Download,
          onClick: () => {
            track(trackingEvents.employeesData.importFromDatev, {
              company_id: company.id,
            })
            handleDatevLaudsEmployeesImport()
          },
        },
        canEditEmployeesInBulk &&
          hasDevUniversalUpdaterEmployeesFeature && {
            label: i18n.t('employees.employees_list.actions.edit_employees_from_file'),
            icon: Download,
            onClick: () => {
              handleClickEditPeopleFromFile()
            },
          },
        canEditContracts &&
          hasDevUniversalUpdaterEmployeesFeature && {
            label: i18n.t('employees.employees_list.actions.edit_agreements_from_file'),
            icon: Download,
            onClick: () => {
              handleClickEditAgreementsFromFile()
            },
          },
        canImportResources && {
          label: i18n.t('employees.employees_list.actions.see_import_history'),
          icon: ClockBack,
          onClick: () => {
            navigate(navigation.settings.universalImporter.fullPath())
          },
        },
      ].filter(Boolean),
      [
        canManageTimeoff && {
          label: i18n.t('employees.employees_list.actions.add_time_off'),
          icon: Add,
          onClick: () => {
            track(trackingEvents.employeesData.addTimeOff, {
              company_id: company.id,
            })
            navigate(navigation.employees.list.bulkAddTimeOff.fullPath())
          },
        },
        !hasDevUniversalContractsEditionFeature &&
          canEditContracts && {
            label: i18n.t('employees.employees_list.actions.edit_contracts'),
            icon: Pencil,
            onClick: () => {
              navigate(navigation.employees.list.importContractsMode.fullPath())
            },
          },
      ].filter(Boolean),
    ].filter((groups) => groups.length > 0)
  }

  const source = useGraphqlDataCollectionSource(
    {
      document: EmployeesCollectionListDocument,
      perPage: 25,
      mapVariables: ({ filters, sortings, search }) => {
        if (filters)
          track(trackingEvents.employeesData.employeesListFiltered, {
            company_id: company.id,
            filters: Object.keys(filters),
          })
        if (search)
          track(trackingEvents.employeesData.employeesListSearched, {
            company_id: company.id,
          })
        const teamIds = filters.team?.map((id) => id)
        const { locationIds, workAreaIds } = extractWorkplaceFilterValues(filters)
        const { jobCatalogTreeNodeIds, jobLevelIds } = extractJobRoleFilterValues(filters)
        const jobTitles = filters.jobTitle?.map((title) => title)
        const legalEntityIds = filters.legalEntity?.map((id) => id)
        const employeeGroupIds = filters.employeeGroup?.map((id) => id)
        const timeoffManagerIds = filters.timeoffManager?.map((id) => id)
        const hiringDateRange = filters?.hiringDate
        const hiringDateFrom =
          hiringDateRange && typeof hiringDateRange === 'object' && 'from' in hiringDateRange
            ? toDateString(hiringDateRange.from)
            : undefined

        const hiringDateTo =
          hiringDateRange && typeof hiringDateRange === 'object' && 'to' in hiringDateRange
            ? toDateString(hiringDateRange.to)
            : undefined

        const contractEndDateRange = filters?.contractEndDate
        const contractEndDateFrom =
          contractEndDateRange &&
          typeof contractEndDateRange === 'object' &&
          'from' in contractEndDateRange
            ? toDateString(contractEndDateRange.from)
            : undefined

        const contractEndDateTo =
          contractEndDateRange &&
          typeof contractEndDateRange === 'object' &&
          'to' in contractEndDateRange
            ? toDateString(contractEndDateRange.to)
            : undefined

        const contractStatus = filters.contractStatus?.map((status) => status)
        const accessStatus = filters.accessStatus?.map((status) => status)
        const promotionStatus = filters.promotionStatus?.map((status) => status)

        // By default, exclude terminated employees unless explicitly filtered
        // If no accessStatus filter is applied, show all statuses except 'terminated'
        const accessStatusWithDefault = accessStatus?.length
          ? accessStatus
          : canSeeFormerEmployees
            ? ['not_invited', 'accepted', 'active', 'invited']
            : undefined

        return {
          companyId: company.id,
          onlyMyReportees: filters.directReports?.includes(true),
          reportingTo: filters.reportingTo?.map((id) => id),
          referenceContractLevelIds: jobLevelIds?.length ? jobLevelIds : undefined,
          sort: sortings?.[0],
          // When fulltext search FF is ON, route search through nameContainsSearch
          // (LIKE '%term%' substring match) instead of fuzzySearch (prefix match).
          // Only one must be sent to avoid AND-chaining that narrows results.
          search: hasFulltextEmployeeSearch ? undefined : search,
          nameContainsSearch: hasFulltextEmployeeSearch ? search : undefined,
          teamIds: teamIds?.length ? teamIds : undefined,
          locationIds: locationIds?.length ? locationIds : undefined,
          workAreaIds: workAreaIds?.length ? workAreaIds : undefined,
          referenceContractJobTitles: jobTitles?.length ? jobTitles : undefined,
          referenceContractTreeLevelUuids: jobCatalogTreeNodeIds?.length
            ? jobCatalogTreeNodeIds
            : undefined,
          legalEntityIds: legalEntityIds?.length ? legalEntityIds : undefined,
          employeeGroupIds: employeeGroupIds?.length ? employeeGroupIds : undefined,
          timeoffManagerIds: timeoffManagerIds?.length ? timeoffManagerIds : undefined,
          referenceContractStartsOnGteq: hiringDateFrom,
          referenceContractStartsOnLteq: hiringDateTo,
          referenceContractEndsOnGteq: contractEndDateFrom,
          referenceContractEndsOnLteq: contractEndDateTo,
          contractStatus: contractStatus?.length ? contractStatus : undefined,
          accessStatus: accessStatusWithDefault,
          promotionStatus: promotionStatus?.length ? promotionStatus : undefined,
        }
      },
      mapPagination: (data) => {
        return {
          total: data.employees.employeesConnection.totalCount ?? 0,
        }
      },
      mapData: (data) => {
        const nodes = data.employees.employeesConnection.nodes

        // Check if user can see ANY job title (for scoped permissions)
        // If global permission is false, check if any employee has the permission
        if (!canSeeJobTitle && nodes.length > 0) {
          const hasAnyPermission = nodes.some(
            (employee) => employee.referenceContract?._policies?.seeJobTitle?.can === true
          )
          if (hasAnyPermission) {
            setCanSeeAnyJobTitle(true)
          }
        }

        return nodes
      },
      itemUrl: (item) => navigation.employees.id.fullPath({ id: item.id }),
      sortings: {
        firstName: {
          label: i18n.t('employee_form.first_name'),
        },
        lastName: {
          label: i18n.t('employee_form.last_name'),
        },
        ...(canSeeStartDate
          ? {
              referenceContractStartsOn: {
                label: i18n.t('employees.employees_list.headers.hired'),
              },
            }
          : {}),
      },
      defaultSortings: {
        field: 'firstName',
        order: 'asc',
      },
      search: {
        enabled: true,
      },
      filters: {
        ...employeeFilters,
        accessStatus: {
          type: 'in',
          label: i18n.t('employees.employees_list.filters.employee_status'),
          options: {
            options: [
              ...(canSeeFormerEmployees
                ? [
                    {
                      label: i18n.t('employees.employees_list.status.terminated'),
                      value: 'terminated',
                    },
                  ]
                : []),
              {
                label: i18n.t('employees.employees_list.status.not_invited'),
                value: 'not_invited',
              },
              {
                label: i18n.t('employees.employees_list.status.accepted'),
                value: 'accepted',
              },
              {
                label: i18n.t('employees.employees_list.status.active'),
                value: 'active',
              },
              {
                label: i18n.t('employees.employees_list.status.invited'),
                value: 'invited',
              },
            ],
          },
        },
        contractStatus: {
          type: 'in',
          label: i18n.t('employees.employees_list.filters.contract_status'),
          options: {
            options: [
              {
                label: i18n.t('employees.employees_list.filters.contract_status_options.ongoing'),
                value: 'ongoing',
              },
              {
                label: i18n.t('employees.employees_list.filters.contract_status_options.upcoming'),
                value: 'upcoming',
              },
              ...(canSeeFormerEmployees
                ? [
                    {
                      label: i18n.t(
                        'employees.employees_list.filters.contract_status_options.ended'
                      ),
                      value: 'ended',
                    },
                  ]
                : []),
              ...(hasDevFixedDiscontinuousContractsFeature
                ? [
                    {
                      label: i18n.t(
                        'employees.employees_list.filters.contract_status_options.in_activity_period'
                      ),
                      value: 'in_activity_period',
                    },
                    {
                      label: i18n.t(
                        'employees.employees_list.filters.contract_status_options.in_inactivity_period'
                      ),
                      value: 'in_inactivity_period',
                    },
                  ]
                : []),
            ],
          },
        },
        ...(canSeePendingPromotions
          ? {
              promotionStatus: {
                type: 'in' as const,
                label: i18n.t('employees.employees_list.filters.promotion_status'),
                options: {
                  options: [
                    {
                      label: i18n.t(
                        'employees.employees_list.filters.promotion_status_options.pending'
                      ),
                      value: 'pending',
                    },
                  ],
                },
              },
            }
          : {}),
        directReports: {
          type: 'in',
          label: i18n.t('employees.employees_list.filters.managed_by_me.title'),
          options: {
            options: [
              {
                label: i18n.t(
                  'employees.employees_list.filters.managed_by_me.options.managed_by_me'
                ),
                value: true,
              },
              {
                label: i18n.t(
                  'employees.employees_list.filters.managed_by_me.options.managed_by_others'
                ),
                value: false,
              },
            ],
          },
        },
        reportingTo: employeeFilters.reportingTo,
        ...(canSeeAnyJobTitle
          ? {
              jobTitle: {
                type: 'in' as const,
                label: i18n.t('employees.employees_list.filters.job_title'),
                options: {
                  options: async () => {
                    const { data } = await client.query({
                      query: EmployeeCollectionFilterJobTitlesDocument,
                      variables: {
                        companyId: company.id,
                      },
                      fetchPolicy: 'cache-first',
                    })

                    const jobTitles = data.employees.employees
                      .map((employee) => {
                        return employee.referenceContract?.jobTitle
                          ? {
                              label: employee.referenceContract?.jobTitle,
                              value: employee.referenceContract?.jobTitle,
                            }
                          : undefined
                      })
                      .filter(Boolean)

                    return uniqBy(jobTitles, (item) => item.value.toLowerCase().trim())
                  },
                },
              },
            }
          : {}),
        employeeGroup: {
          type: 'in',
          label: i18n.t('employees.employees_list.filters.employee_group'),
          options: {
            options: async () => {
              const { data } = await client.query({
                query: EmployeeCollectionFilterEmployeeGroupNamesDocument,
                variables: {
                  companyId: company.id,
                },
                fetchPolicy: 'cache-first',
              })

              return data.companies.legalEntitiesConnection.nodes
                .map((legalEntity) => {
                  return legalEntity.employeeGroupsConnection.nodes.map((employeeGroup) => ({
                    label: employeeGroup.name,
                    value: employeeGroup.id,
                  }))
                })
                .flat()
            },
          },
        },
        timeoffManager: employeeFilters.timeoffManager,
        ...(canSeeStartDate
          ? {
              hiringDate: {
                type: 'date',
                label: i18n.t('employees.employees_list.filters.hiring_date'),
                options: {
                  mode: 'range',
                },
              },
            }
          : {}),
        ...(canSeeEndDate
          ? {
              contractEndDate: {
                type: 'date',
                label: i18n.t('employees.employees_list.filters.contract_end_date'),
                options: {
                  mode: 'range',
                },
              },
            }
          : {}),
      },
      presets: [],
      primaryActions,
      secondaryActions: {
        expanded: canExportData ? 1 : 0,
        actions: secondaryActions,
      },
      selectable: (item) => item.id,
      allPagesSelection: true,
      bulkActions: (selectedRows) => {
        const selectedEmployees = selectedRows.itemsStatus.filter((item) => item.checked)
        const hasContractPermissions =
          selectedEmployees.length > 0 &&
          !selectedEmployees.find((item) => !item.item._policies?.editContract?.can)
        const hasTerminatePermissions =
          selectedEmployees.length > 0 &&
          !selectedEmployees.find((item) => !item.item._policies?.terminateEmployees?.can)

        const hasAnyPausableDiscontinuous =
          selectedEmployees.length > 0 &&
          !!selectedEmployees.find((item) => {
            const flags = readContractFlags(item.item.referenceContract?.contract)
            return !!(flags.isDiscontinuous && flags.isPausable)
          })

        const hasAnyResumableDiscontinuous =
          selectedEmployees.length > 0 &&
          !!selectedEmployees.find((item) => {
            const flags = readContractFlags(item.item.referenceContract?.contract)
            return flags.isDiscontinuous && flags.isResumable
          })

        return {
          primary: [
            hasDevUniversalContractsEditionFeature &&
              canEditContracts && {
                id: 'edit-contracts',
                label: i18n.t('employees.employees_list.actions.edit_agreement'),
                icon: Pencil,
              },
            (!hasPayrollManagementModule || hasCompensationsModule) &&
              hasContractPermissions && {
                id: 'compensations',
                label: i18n.t('employees.employees_list.actions.add_compensations'),
                icon: Pencil,
              },
            hasContractPermissions &&
              hasAnyPausableDiscontinuous && {
                id: 'end-activity-period',
                label: i18n.t('employees.employees_list.actions.end_activity_period'),
              },
            hasContractPermissions &&
              hasAnyResumableDiscontinuous && {
                id: 'start-activity-period',
                label: i18n.t('employees.employees_list.actions.start_activity_period'),
              },
            hasContractPermissions &&
              hasDevFixedDiscontinuousContractsFeature && {
                id: 'set-version-start-date',
                label: i18n.t('employees.employees_list.actions.set_contract_start_date'),
                icon: Pencil,
              },
            hasPayrollManagementModule &&
              !hasCompensationsModule &&
              hasContractPermissions && {
                id: 'supplements',
                label: i18n.t('employees.employees_list.actions.supplements'),
              },
          ].filter(Boolean),
          secondary: [
            canEditEmployeesInBulk && {
              id: 'edit-employees',
              label: i18n.t('employees.employees_list.actions.edit_employee'),
              icon: Person,
            },
            hasTerminatePermissions && {
              id: 'terminate-employees',
              label: i18n.t('employees.employees_list.actions.terminate_employees'),
              icon: Delete,
              critical: true,
            },
          ].filter(Boolean),
        }
      },
      itemActions: (item) => [
        {
          label: i18n.t('employees.employees_list.actions.become', { name: item.firstName }),
          enabled: !!hasBecomeEnabledFeature && !!item.access?._policies?.become?.can,
          onClick: () => {
            handleBecome(item)
          },
        },
        {
          label: i18n.t('employees.employees_list.actions.invite_employee'),
          enabled: !!item._policies?.inviteEmployeeToApp?.can && !item.access?.invitedOn,
          onClick: () => {
            handleInviteEmployee(item, handleToggleRefetch)
          },
        },
        {
          label: i18n.t('employees.employees_list.actions.resend_invitation'),
          enabled:
            !!item._policies?.inviteEmployeeToApp?.can &&
            !!item.access?.invitedOn &&
            !item.access?.isConfirmed,
          onClick: () => {
            handleInviteEmployee(item)
          },
        },
        {
          label: i18n.t('employees.employees_list.actions.activate_employee'),
          enabled: !!item._policies?.inviteEmployeeToApp?.can && !!item.terminatedOn,
          onClick: () => {
            handleActivateEmployee(item, handleToggleRefetch)
          },
        },
        {
          label: i18n.t('employees.employees_list.actions.set_contract_start_date'),
          enabled:
            !!hasDevFixedDiscontinuousContractsFeature && !!item._policies?.editContract?.can,
          onClick: () => {
            handleRequestBulkFDContracts([item])
          },
        },
        {
          label: i18n.t('employees.employees_list.actions.edit_employee'),
          enabled: !!item._policies?.editPersonalInfo.can,
          onClick: () => {
            navigate(navigation.employees.id.personal.fullPath({ id: item.id }))
          },
        },
        ...(item._policies?.terminateEmployees?.can && !item.terminatedOn
          ? [
              { type: 'separator' as const },
              {
                label: i18n.t('employees.employees_list.actions.terminate_employee'),
                enabled: true,
                icon: Delete,
                critical: true,
                onClick: () => {
                  navigate(navigation.employees.id.terminate.fullPath({ id: item.id }))
                },
              },
            ]
          : []),
      ],
    },
    [
      company.id,
      hasJobCatalogTreeFeature,
      hasDevFixedDiscontinuousContractsFeature,
      canSeeFormerEmployees,
      hasFulltextEmployeeSearch,
      refetch,
    ]
  )

  const handleBulkAction = async (
    action: string,
    {
      allSelected,
      itemsStatus,
      filters,
    }: {
      allSelected: boolean | 'indeterminate'
      itemsStatus: readonly {
        item: EmployeeCollectionListEmployeeFragment
        checked: boolean
      }[]
      selectedIds: readonly (string | number)[]
      filters: BulkActionFilters
    }
  ) => {
    const selectedEmployees = await getSelectedEmployees({
      allSelected,
      itemsStatus,
      filters,
    })

    switch (action) {
      case 'edit-employees':
        handleClickEmployeesBulkEdition(selectedEmployees)
        break
      case 'edit-contracts':
        handleClickContractsBulkEdition(selectedEmployees)
        break
      case 'compensations':
        handleClickAdditionalCompensationsBulkEdition(selectedEmployees)
        break
      case 'supplements':
        handleClickSupplementsBulkEdition(selectedEmployees)
        break
      case 'terminate-employees':
        {
          const eligibleIds = selectedEmployees
            .filter((item) => !item.terminatedOn)
            .map((item) => item.id)

          const terminatedCount = selectedEmployees.length - eligibleIds.length

          // Track Bulk Termination Start
          track(trackingEvents.bulkTermination.start, {
            company_id: company.id,
            selected_count: selectedEmployees.length,
            eligible_count: eligibleIds.length,
            already_terminated_count: terminatedCount,
          })

          handleRequestBulkTerminate?.({
            selectedEmployeeIds: selectedEmployees.map((item) => item.id),
            eligibleEmployeeIds: eligibleIds,
            terminatedSelectedCount: terminatedCount,
          })
        }
        break
      case 'end-activity-period':
        {
          // Eligible employees: fixed-discontinuous AND isPausable (same as single pause)
          const eligible = selectedEmployees.filter((emp) => {
            const flags = readContractFlags(emp.referenceContract?.contract)
            return !!(flags.isDiscontinuous && flags.isPausable)
          })

          // Track Bulk Pause Start
          track(trackingEvents.bulkPause.start, {
            company_id: company.id,
            selected_count: selectedEmployees.length,
            eligible_count: eligible.length,
            ineligible_count: selectedEmployees.length - eligible.length,
          })

          handleRequestBulkPause?.({
            selectedEmployees,
            eligibleEmployees: eligible,
          })
        }
        break
      case 'start-activity-period':
        {
          // Eligible employees: fixed-discontinuous AND resumable
          const eligible = selectedEmployees.filter((emp) => {
            const flags = readContractFlags(emp.referenceContract?.contract)
            return flags.isDiscontinuous && flags.isResumable
          })

          // Track Bulk Activate Start
          track(trackingEvents.bulkActivate.start, {
            company_id: company.id,
            selected_count: selectedEmployees.length,
            eligible_count: eligible.length,
            ineligible_count: selectedEmployees.length - eligible.length,
          })

          handleRequestBulkActivate?.({
            selectedEmployees,
            eligibleEmployees: eligible,
          })
        }
        break
      case 'set-version-start-date':
        handleRequestBulkFDContracts(selectedEmployees)
        break
    }
  }

  return (
    <>
      <OneDataCollection
        id="organization/employees/v1"
        source={source}
        onBulkAction={handleBulkAction}
        visualizations={[
          {
            type: 'table',
            options: {
              frozenColumns: 2,
              allowColumnReordering: true,
              allowColumnHiding: true,
              columns: [
                {
                  label: i18n.t('employees.employees_list.headers.employee'),
                  sorting: 'firstName',
                  render: (item) => ({
                    type: 'person',
                    value: {
                      firstName: item.firstName,
                      lastName: item.lastName,
                      name: item.fullName ?? '',
                      src: buildUrl(item.access?.avatar ?? '') ?? '',
                    },
                  }),
                  noHiding: true,
                },
                ...(canSeeAnyJobTitle
                  ? [
                      {
                        label: i18n.t('employees.employees_list.headers.job'),
                        render: (item: EmployeeCollectionListEmployeeFragment) => {
                          const canSeeEmployeeJobTitle =
                            item.referenceContract?._policies?.seeJobTitle?.can ?? false

                          if (!canSeeEmployeeJobTitle) {
                            return lockIconRenderer
                          }

                          const referenceContract = item.referenceContract
                          const preferredJobTitle = referenceContract?.jobTitle ?? ''

                          if (preferredJobTitle) {
                            return preferredJobTitle // stored in the reference contract
                          }

                          // fallback to job catalog data, but it requires extra permission
                          if (hasLegacyJobCatalog) {
                            if (!canSeeJobCatalog) {
                              return lockIconRenderer
                            }

                            if (!referenceContract?.jobCatalogLevel) {
                              return ''
                            }

                            const jobCatalogLegacyTitle = referenceContract?.jobCatalogLevel
                              .isDefault
                              ? referenceContract?.jobCatalogLevel?.roleName
                              : `${referenceContract?.jobCatalogLevel?.roleName} / ${referenceContract?.jobCatalogLevel?.name}`
                            return jobCatalogLegacyTitle
                          } else {
                            // Job Catalog Tree
                            if (!canSeeJobCatalogTree) {
                              return lockIconRenderer
                            }

                            if (!referenceContract?.jobCatalogTreeLevel) {
                              return ''
                            }

                            const jobCatalogTreeTitle = `${referenceContract.jobCatalogTreeLevel?.ancestor?.name} / ${referenceContract.jobCatalogTreeLevel.name}`
                            return jobCatalogTreeTitle
                          }
                        },
                      },
                    ]
                  : []),
                {
                  label: i18n.t('employees.employees_list.headers.workplace'),
                  render: (item) => item.defaultLocation?.name ?? '',
                },
                ...(hasFulltextEmployeeSearch
                  ? [
                      {
                        label: i18n.t('employees.employees_list.headers.identifier'),
                        hidden: true,
                        render: (item: EmployeeCollectionListEmployeeFragment) => {
                          const canSeeIdentifier = item._policies?.readPersonalInfo?.can ?? false

                          if (!canSeeIdentifier) return lockIconRenderer

                          return formatIdentifier(item.identifier, item.identifierType)
                        },
                      },
                      {
                        label: i18n.t('employees.employees_list.headers.company_identifier'),
                        hidden: true,
                        render: (item: EmployeeCollectionListEmployeeFragment) => {
                          const canSeeCompanyIdentifier =
                            item._policies?.readCompanyIdentifier?.can ?? false

                          if (!canSeeCompanyIdentifier) return lockIconRenderer

                          return item.companyIdentifier ?? ''
                        },
                      },
                    ]
                  : []),
                ...(canSeeStartDate
                  ? [
                      {
                        label: i18n.t('employees.employees_list.headers.hired'),
                        sorting: 'referenceContractStartsOn' as const,
                        render: (item: EmployeeCollectionListEmployeeFragment) => {
                          const canSeeEmployeeStartDate =
                            item.referenceContract?._policies?.seeStartDate?.can ?? false

                          if (!canSeeEmployeeStartDate) {
                            return lockIconRenderer
                          }

                          const timeAgo = item.referenceContract?.startsOn
                            ? dateBackendDateTimeToRelativeTime(
                                i18nDateToString(
                                  moment(item.referenceContract?.startsOn),
                                  'backend'
                                )
                              )
                            : ''

                          return {
                            type: 'date' as const,
                            value: timeAgo,
                          }
                        },
                      },
                    ]
                  : []),
                {
                  label: i18n.t('employees.employees_list.headers.access'),
                  render: (item) => {
                    const { access, terminatedOn, referenceContract } = item
                    const today = new Date()

                    const getAccessStatus = () => {
                      // Only show terminated status if user has permission to see former employees
                      // NOTE: This is UI-level protection. For full security, the backend should
                      // also filter the terminatedOn field based on SeeFormerEmployees permission
                      // to prevent users from seeing it in the GraphQL response via DevTools.
                      if (
                        terminatedOn &&
                        stringToDate(terminatedOn, 'backend').isBefore(today) &&
                        canSeeFormerEmployees
                      ) {
                        return {
                          label: i18n.t('employees.employees_list.status.terminated'),
                        }
                      }

                      if (!access?.invitedOn) {
                        return {
                          label: i18n.t('employees.employees_list.status.not_invited'),
                        }
                      }

                      if (
                        access?.isConfirmed &&
                        referenceContract?.startsOn &&
                        stringToDate(referenceContract.startsOn, 'backend').isAfter(today)
                      ) {
                        return {
                          label: i18n.t('employees.employees_list.status.accepted'),
                        }
                      }

                      if (access.isConfirmed) {
                        return {
                          label: i18n.t('employees.employees_list.status.active'),
                        }
                      }

                      if (access.invitedOn) {
                        return {
                          label: i18n.t('employees.employees_list.status.invited'),
                        }
                      }

                      return {
                        label: i18n.t('employees.employees_list.status.not_invited'),
                      }
                    }

                    const accessStatus = getAccessStatus()

                    return {
                      type: 'tag',
                      value: accessStatus,
                    }
                  },
                },
                {
                  label: i18n.t('employees.employees_list.headers.contract_status'),
                  render: (item) => {
                    const contractStatus = getContractStatus(
                      item.terminatedOn,
                      item.referenceContract
                    )

                    const getStatusLabel = (status: string) => {
                      switch (status) {
                        case 'ongoing':
                          return i18n.t('employees.employees_list.contract_status.ongoing')
                        case 'ended':
                          return i18n.t('employees.employees_list.contract_status.ended')
                        case 'upcoming':
                          return i18n.t('employees.employees_list.contract_status.upcoming')
                        case 'in_activity_period':
                          return i18n.t(
                            'employees.employees_list.contract_status.in_activity_period'
                          )
                        case 'in_inactivity_period':
                          return i18n.t(
                            'employees.employees_list.contract_status.in_inactivity_period'
                          )
                        default:
                          return i18n.t('employees.employees_list.contract_status.ongoing')
                      }
                    }

                    return {
                      type: 'status',
                      value: {
                        label: getStatusLabel(contractStatus),
                        status:
                          contractStatus === 'ongoing' || contractStatus === 'in_activity_period'
                            ? 'positive'
                            : contractStatus === 'ended'
                              ? 'critical'
                              : contractStatus === 'upcoming'
                                ? 'warning'
                                : 'neutral',
                      },
                    }
                  },
                },
              ],
            },
          },
        ]}
      />
    </>
  )
}

export default CollectionsPage
